import React from 'react';
import { Typography, Grid, Box, Card } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import AppsIcon from '@mui/icons-material/Apps';
import RouterIcon from '@mui/icons-material/Router';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import TransformIcon from '@mui/icons-material/Transform';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import FolderIcon from '@mui/icons-material/Folder';

const ArchitectureSection = () => {
  const sources = [
    { name: 'Firewalls', icon: SecurityIcon },
    { name: 'EDR/XDR', icon: SecurityIcon },
    { name: 'Cloud Logs', icon: CloudQueueIcon },
    { name: 'Applications', icon: AppsIcon },
    { name: 'Network Devices', icon: RouterIcon }
  ];

  const capabilities = [
    { name: 'Smart Filtering', icon: FilterAltIcon },
    { name: 'Normalization', icon: TransformIcon },
    { name: 'Enrichment', icon: EnhancedEncryptionIcon },
    { name: 'Routing', icon: AltRouteIcon },
    { name: 'Compliance Storage', icon: FolderIcon }
  ];

  const destinations = [
    { name: 'Splunk', color: '#10b981' },
    { name: 'Elastic', color: '#10b981' },
    { name: 'Chronicle', color: '#10b981' },
    { name: 'ArcSight', color: '#10b981' },
    { name: 'Data Lake', color: '#10b981' },
    { name: 'Threat Intel', color: '#10b981' }
  ];

  return (
    <section id="architecture" className="whitepaper-section">
      <Typography variant="h2" className="section-title">
        Security Data Fabric Architecture
      </Typography>
      
      <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 1.8, mb: 5, color: '#e0e7ef', textAlign: 'center', maxWidth: '1000px', mx: 'auto' }}>
        The Security Data Fabric pattern creates a centralized, streaming data layer that sits between security data sources and consuming systems. Built on <strong style={{ color: '#60a5fa' }}>Apache Kafka</strong> and <strong style={{ color: '#60a5fa' }}>Confluent Platform</strong>, this architecture decouples data producers from consumers, enabling independent scaling, multiple analytics tools, and cost optimization.
      </Typography>

      <Box sx={{ mb: 4, position: 'relative' }}>
        <Grid container spacing={3} alignItems="center">
          {/* Data Sources Column */}
          <Grid item xs={12} md={3.5}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                mb: 3, 
                color: '#60a5fa',
                fontSize: '1.5rem',
                pb: 2,
                borderBottom: '3px solid rgba(96,165,250,0.3)'
              }}>
                Data Sources
              </Typography>
            </Box>
            <Box>
              {sources.map((source, idx) => {
                const IconComponent = source.icon;
                return (
                  <Card key={source.name} sx={{
                    mb: 2,
                    background: 'linear-gradient(135deg, rgba(96,165,250,0.1) 0%, rgba(59,130,246,0.05) 100%)',
                    border: '2px solid rgba(96,165,250,0.3)',
                    borderRadius: '12px',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(8px)',
                      boxShadow: '0 8px 32px rgba(96,165,250,0.3)',
                      borderColor: 'rgba(96,165,250,0.5)',
                      background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)'
                    }
                  }}>
                    <Box sx={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 4px 16px rgba(96,165,250,0.4)'
                    }}>
                      <IconComponent sx={{ color: '#fff', fontSize: '1.5rem' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#e0e7ef', fontSize: '1.05rem' }}>
                      {source.name}
                    </Typography>
                  </Card>
                );
              })}
            </Box>
          </Grid>
          
          {/* Arrow 1 */}
          <Grid item xs={12} md={0.5}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
              <ArrowForwardIcon sx={{ 
                fontSize: '3rem', 
                color: '#60a5fa',
                transform: { xs: 'rotate(90deg)', md: 'none' }
              }} />
            </Box>
          </Grid>

          {/* Confluent Center Column */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(96,165,250,0.2) 0%, rgba(16,185,129,0.15) 100%)',
              border: '3px solid #60a5fa',
              borderRadius: '24px',
              p: 4,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 16px 48px rgba(96,165,250,0.4)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at center, rgba(96,165,250,0.2) 0%, transparent 70%)',
                pointerEvents: 'none'
              }
            }}>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #10b981 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 8px 32px rgba(96,165,250,0.5)',
                  border: '4px solid rgba(10,34,64,1)'
                }}>
                  <StorageIcon sx={{ color: '#fff', fontSize: '2.5rem' }} />
                </Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 800, 
                  mb: 3, 
                  color: '#60a5fa',
                  fontSize: '1.8rem'
                }}>
                  Confluent Security Data Fabric
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {capabilities.map((cap) => {
                    const IconComponent = cap.icon;
                    return (
                      <Box key={cap.name} sx={{
                        background: 'rgba(10,34,64,0.6)',
                        borderRadius: '12px',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        border: '1px solid rgba(96,165,250,0.3)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(10,34,64,0.8)',
                          borderColor: 'rgba(96,165,250,0.5)',
                          transform: 'scale(1.02)'
                        }
                      }}>
                        <Box sx={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <IconComponent sx={{ color: '#fff', fontSize: '1.2rem' }} />
                        </Box>
                        <Typography sx={{ 
                          color: '#e0e7ef', 
                          fontWeight: 600,
                          fontSize: '1.05rem',
                          textAlign: 'left'
                        }}>
                          {cap.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Arrow 2 */}
          <Grid item xs={12} md={0.5}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
              <ArrowForwardIcon sx={{ 
                fontSize: '3rem', 
                color: '#10b981',
                transform: { xs: 'rotate(90deg)', md: 'none' }
              }} />
            </Box>
          </Grid>

          {/* Destinations Column */}
          <Grid item xs={12} md={3.5}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                mb: 3, 
                color: '#10b981',
                fontSize: '1.5rem',
                pb: 2,
                borderBottom: '3px solid rgba(16,185,129,0.3)'
              }}>
                Destinations
              </Typography>
            </Box>
            <Box>
              {destinations.map((dest, idx) => (
                <Card key={dest.name} sx={{
                  mb: 2,
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.05) 100%)',
                  border: '2px solid rgba(16,185,129,0.3)',
                  borderRadius: '12px',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateX(-8px)',
                    boxShadow: '0 8px 32px rgba(16,185,129,0.3)',
                    borderColor: 'rgba(16,185,129,0.5)',
                    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)'
                  }
                }}>
                  <Box sx={{
                    width: '45px',
                    height: '45px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 4px 16px rgba(16,185,129,0.4)'
                  }}>
                    <StorageIcon sx={{ color: '#fff', fontSize: '1.5rem' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: '#e0e7ef', fontSize: '1.05rem' }}>
                    {dest.name}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </section>
  );
};

export default ArchitectureSection;
